import { MouseEvent, useEffect, useLayoutEffect, useState } from "react";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { dynamicFormTabIndexAF, RideTabsEnum, RydeUiUnit } from "@ryde/shared";

interface IUseRideActiveTabParams {
  entity: RydeUiUnit;
  rideRecordId?: number;
  isMobile: boolean;
  showIncidentsRideTab: boolean;
}

interface IUseRideActiveTabResult {
  activeTab: RideTabsEnum;
  handleTabSwitch: (
    event: MouseEvent<HTMLElement>,
    nextView: RideTabsEnum | null,
  ) => void;
}

export const useRideActiveTab = ({
  entity,
  rideRecordId,
  isMobile,
  showIncidentsRideTab,
}: IUseRideActiveTabParams): IUseRideActiveTabResult => {
  const setSchemaTabIndex = useSetRecoilState(dynamicFormTabIndexAF(entity));
  const [activeTab, setActiveTab] = useState<RideTabsEnum>(RideTabsEnum.Basic);

  const selectTab = useEvent((tab: RideTabsEnum): void => {
    setActiveTab(tab);

    /* Incidents is UI-local and has no schema tab, so leave the index on the last
       schema-backed tab instead of pointing the controls selector past its layout. */
    if (tab <= RideTabsEnum.Map) {
      setSchemaTabIndex(tab);
    }
  });

  /* The schema tab index is shared per entity, so without this reset a card opens on the tab
     the previously opened card was left on. Layout effect because the controls selector reads
     that same index: a passive effect paints one frame of the old tab's fields under Basic. */
  useLayoutEffect(() => {
    selectTab(RideTabsEnum.Basic);
  }, [rideRecordId, selectTab]);

  useEffect(() => {
    if (!isMobile && activeTab === RideTabsEnum.Map) {
      selectTab(RideTabsEnum.Basic);
    }
  }, [activeTab, isMobile, selectTab]);

  useEffect(() => {
    if (!showIncidentsRideTab && activeTab === RideTabsEnum.Incidents) {
      selectTab(RideTabsEnum.Basic);
    }
  }, [activeTab, selectTab, showIncidentsRideTab]);

  const handleTabSwitch = useEvent(
    (_: MouseEvent<HTMLElement>, nextView: RideTabsEnum | null): void => {
      if (nextView === null) {
        return;
      }

      selectTab(nextView);
    },
  );

  return { activeTab, handleTabSwitch };
};
